import { clsx } from "clsx";
import React, { forwardRef } from "react";
import { twMerge } from "tailwind-merge";

import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner";
import { ConditionalTooltip } from "../Tooltip/Tooltip";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "outline"
  | "ghost"
  | "link"
  | "destructive"
  | "link-destructive"
  | "icon-primary"
  | "icon-secondary"
  | "icon-outline"
  | "icon-ghost";

export type ButtonSize = "xs" | "sm" | "md" | "lg";

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  className?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  tooltipContent?: React.ReactNode;
  loading?: boolean;
};

const baseClasses =
  "border-box inline-flex items-center justify-center gap-1.5 rounded-md font-medium whitespace-nowrap outline-blue-300 transition-all not-disabled:cursor-pointer focus:outline-2 disabled:opacity-30";

const iconOnlyVariants = new Set<ButtonVariant>([
  "icon-primary",
  "icon-secondary",
  "icon-outline",
  "icon-ghost",
]);

type SizeTokens = {
  rect: string;
  icon: string;
  spinner: string;
  // Default size class applied to icons in leadingIcon/trailingIcon slots
  // (and to children for icon-only variants). Per front/src/AGENTS.md, icons
  // <= 16px pair with strokeWidth={2} (lucide's default), so we don't need
  // to override stroke. Callers can override by passing their own h-/w-
  // class on the icon — twMerge resolves to the caller's class.
  iconSize: string;
};

const sizeStyles: Record<ButtonSize, SizeTokens> = {
  xs: {
    rect: "h-8 px-3 py-2 text-xs leading-4",
    icon: "size-8",
    spinner: "size-3",
    iconSize: "h-3 w-3",
  },
  sm: {
    rect: "h-9 px-4 py-2.5 text-xs leading-4",
    icon: "size-9",
    spinner: "size-3.5",
    iconSize: "h-3.5 w-3.5",
  },
  md: {
    rect: "h-10 px-5 py-2.5 text-sm leading-5",
    icon: "size-10",
    spinner: "size-4",
    iconSize: "h-4 w-4",
  },
  lg: {
    rect: "h-11 px-6 py-3 text-base leading-5",
    icon: "size-11",
    spinner: "size-[18px]",
    iconSize: "h-[18px] w-[18px]",
  },
};

// Apply a default size class to an icon element while letting the caller
// override by passing their own h-/w- class. twMerge resolves conflicts in
// favor of the caller's class. Non-element values (e.g. strings, null) are
// returned as-is.
const withDefaultIconSize = (
  icon: React.ReactNode,
  defaultClass: string,
): React.ReactNode => {
  if (!React.isValidElement(icon)) return icon;
  const element = icon as React.ReactElement<{ className?: string }>;
  return React.cloneElement(element, {
    className: twMerge(defaultClass, element.props.className),
  });
};

const filledPrimary =
  "bg-blue-500 text-white not-disabled:hover:bg-blue-600 not-disabled:active:bg-blue-700";
const filledSecondary =
  "bg-gray-100 text-gray-800 not-disabled:hover:bg-gray-200 not-disabled:active:bg-gray-300";
const outlined =
  "bg-white text-gray-800 border border-gray-300 not-disabled:hover:bg-gray-100 not-disabled:active:bg-gray-200";
const ghostNeutral =
  "bg-transparent text-gray-800 not-disabled:hover:bg-gray-100 not-disabled:active:bg-gray-200";

const variantStyles: Record<ButtonVariant, string> = {
  primary: filledPrimary,
  secondary: filledSecondary,
  outline: outlined,
  ghost: ghostNeutral,
  link: "bg-transparent text-blue-500 px-0 not-disabled:hover:underline not-disabled:hover:text-blue-600 not-disabled:active:text-blue-700",
  destructive:
    "bg-red-600 text-white not-disabled:hover:bg-red-700 not-disabled:active:bg-red-800",
  "link-destructive":
    "bg-transparent text-red-700 px-0 not-disabled:hover:underline not-disabled:hover:text-red-800 not-disabled:active:text-red-900",
  "icon-primary": filledPrimary,
  "icon-secondary": filledSecondary,
  "icon-outline": outlined,
  "icon-ghost": ghostNeutral,
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      leadingIcon,
      trailingIcon,
      variant = "primary",
      size = "md",
      children,
      type = "button",
      tooltipContent,
      loading,
      ...rest
    },
    ref,
  ) => {
    const isIconOnly = iconOnlyVariants.has(variant);
    const sizes = sizeStyles[size];

    const buttonItself = (
      <button
        ref={ref}
        {...rest}
        disabled={rest.disabled || loading}
        type={type}
        className={twMerge(
          clsx(
            baseClasses,
            isIconOnly ? `shrink-0 p-0 ${sizes.icon}` : sizes.rect,
            variantStyles[variant],
          ),
          className,
          rest.disabled && "cursor-not-allowed",
        )}
      >
        {loading ? (
          <LoadingSpinner className={sizes.spinner} />
        ) : isIconOnly ? (
          withDefaultIconSize(children, sizes.iconSize)
        ) : (
          <>
            {withDefaultIconSize(leadingIcon, sizes.iconSize)}
            {children}
            {withDefaultIconSize(trailingIcon, sizes.iconSize)}
          </>
        )}
      </button>
    );

    return (
      <ConditionalTooltip condition={!!tooltipContent} content={tooltipContent}>
        {buttonItself}
      </ConditionalTooltip>
    );
  },
);
